/// <reference types="@types/segment-analytics" />
import {
  BasicAddressDetails,
  DataSource,
  DwellingInformation,
  JiraStatuses,
  PolicyCoverage,
  PolicyHolder,
  PolicyVehicle,
} from './types/owl';
import type { Job, JobInformation } from 'bull';

import * as OWL from './owl';
import { Carrier } from './owl';
import { AxiosError, AxiosResponse } from 'axios';
import {
  Coverage,
  Discount,
  DriverResource,
  PrimaryAddress,
} from './clients/dynamo';

export interface ProxyConfig {
  bypass?: string;
  server: string;
  username: string;
  password: string;
}

export interface ObjectUrl {
  file: string;
  url: string;
}

export enum ParrotLinkParams {
  ShowAcceleratedUnderwritingQuestions = 'showAcceleratedUnderwritingQuestions',
}

export enum ArtifactType {
  Screenshot = 'screenshot',
  Trace = 'trace',
  HAR = 'har',
  PolicyRawData = 'policyRawData',
}

export type CarrierPageStateResponse = CarrierPageState & {
  submissionId: string;
  debuggerUrl?: string; // Browserbase debugger URL for Hartford submissions
};

export type PageState = Omit<CarrierPageState, 'carrierSlug'>;

export enum BrowserName {
  chromium = 'chromium',
  webkit = 'webkit',
  firefox = 'firefox',
}

export enum BrowserPurpose {
  Validation = 'validation',
  Automation = 'automation',
  KeepAlive = 'keep-alive',
}

export type BrowserId = `${BrowserPurpose}-${string}`;

export interface CarrierPageState {
  /** Time the state is saved in unix format */
  time?: number;
  sessionStorage: string;
  storageState: string;
  url: string;
  proxyConfig?: ProxyConfig;
  carrierSlug: string;
  browser?: BrowserName;
  ip?: string;
  /** Asteroid execution ID for carriers using Asteroid AI agent */
  asteroidExecutionId?: string;
  /** Asteroid agent that started the execution */
  asteroidAgentId?: string;
  /** Set when the prior-term dec pages agent was started */
  priorDecPagesYears?: number;
}

export interface BasePostLoginJobData {
  clientId: string;
  submissionId: string;
  /** Origin session traceId, threaded from the Client through automation → parser. */
  traceId?: string;
}

export interface AutomationJobData extends BasePostLoginJobData {
  pageState: PageState;
  includeSessionStorage?: boolean;
  carrierSlug: string;
  attempt: number;
  proxyBypass?: string;
  browser?: BrowserName;
}

export type KeepAliveJobData = Omit<AutomationJobData, 'pageState'>;

export interface KeepAliveResponse {
  jobs: Array<Job<KeepAliveJobData>>;
  repeatableJobs: JobInformation[];
}

export enum ResultType {
  /** When mfa code is wrong, don't go back to start of validation */
  Retry = 'retry',
  /** There was an error on our side */
  Error = 'error',
  /** The login validation was invalid */
  Failure = 'failure',
  /** Everything Worked */
  Success = 'success',
  /** We have error selectors, but no success selectors. So we don't know if it worked, but we know when it didn't work  */
  PartiallyImplementedNonFailure = 'partially_implemented_non_failure',
  /** There is mfa */
  Challenge = 'challenge',
  Pass = 'pass',
}

export type OwlResult =
  | ResultType.Error
  | ResultType.Failure
  | ResultType.Challenge
  | ResultType.Success;

export enum FieldType {
  Radio = 'radio',
  Text = 'text',
  PingCode = 'ping-code',
  Email = 'email',
  Password = 'password',
  Select = 'select',
  Checkbox = 'checkbox',
  Phone = 'phone',
  BinarySelect = 'binary-select',
  Number = 'number',
  Address = 'address',
  AutocompleteAddress = 'autocomplete-address',
  Driver = 'driver',
  Dog = 'dog',
  Roof = 'roof',
  Pool = 'pool',
  Occupancy = 'occupancy',
  TextArea = 'text-area',
  ClientPersonal = 'client-personal',
  ClientOccupation = 'client-occupation',
  InlineSelect = 'inline-select',
}

interface BaseField<T extends FieldType> {
  readonly type: T;
  label: string;
  name: string;
  visibleWhen?: { field: string; value: string };
}

export enum SelectorType {
  InnerText = 'innerText',
  Selector = 'selector',
}

export interface RadioOption {
  label: string;
  value: string;
  selectorType?: SelectorType;
}

export interface Radio extends BaseField<FieldType.Radio> {
  options: RadioOption[];
}

export interface Select extends BaseField<FieldType.Select> {
  options: { label: string; value: string }[];
}

export interface BinarySelect extends BaseField<FieldType.BinarySelect> {
  values: BinarySelectValues;
}

export interface NumberField extends BaseField<FieldType.Number> {
  options: { min?: number; max?: number; step?: number };
}

export type Text = BaseField<FieldType.Text>;

export type Email = BaseField<FieldType.Email>;

export type Checkbox = BaseField<FieldType.Checkbox>;

export type Password = BaseField<FieldType.Password>;

export type Phone = BaseField<FieldType.Phone>;

export type AddressField = BaseField<FieldType.Address>;
export type AutocompleteAddressField = BaseField<FieldType.AutocompleteAddress>;

export type DriverField = BaseField<FieldType.Driver>;

export type DogField = BaseField<FieldType.Dog>;

export type RoofField = BaseField<FieldType.Roof>;
export type PoolField = BaseField<FieldType.Pool>;
export type OccupancyField = BaseField<FieldType.Occupancy>;
export type ClientPersonalField = BaseField<FieldType.ClientPersonal>;
export type ClientOccupationField = BaseField<FieldType.ClientOccupation>;
export type TextAreaField = BaseField<FieldType.TextArea>;

export interface InlineSelect extends BaseField<FieldType.InlineSelect> {
  options: { label: string; value: string }[];
}

export type Field =
  | Text
  | Radio
  | Password
  | Select
  | Checkbox
  | NumberField
  | Email
  | Phone
  | BinarySelect
  | AddressField
  | AutocompleteAddressField
  | RoofField
  | PoolField
  | DogField
  | OccupancyField
  | DriverField
  | ClientPersonalField
  | ClientOccupationField
  | TextAreaField
  | InlineSelect;

export type Form = Field[];

export enum LineType {
  Personal = 'personal',
  Commercial = 'commercial',
}

export enum AppType {
  PolicyConnect = 'policy-connect',
  PolicyCancel = 'policy-cancel',
  Demo = 'demo',
}

export interface BinarySelectOption {
  label: string;
  value: boolean;
}

export type BinarySelectValues = [
  { label: 'Yes'; value: true },
  { label: 'No'; value: false },
];

export interface AuthMessage {
  flow: string;
  carrierSlug: string;
  data?: Record<string, string>;
  clientId?: string;
  submissionId: string;
}

export type OwlResponse<T> = AxiosResponse<T>;
export type OwlError<E = { errors: Record<string, string>; message: string }> =
  AxiosError<E>;

export interface JiraTicketStatus {
  self: string;
  id: string;
  iconUrl: string;
  name: JiraStatuses;
  statusCategory: Record<string, string>;
}

export interface JiraTicketPriority {
  self: string;
  id: string;
  iconUrl: string;
  name: string;
}

export interface JiraSearchResponse {
  affectedClientSubmissions: string[];
  issueKey: string;
  type: string;
  summary: string;
  description: string | null;
  status: JiraTicketStatus;
  priority: JiraTicketPriority;
}

export interface CarrierDataExtracted {
  carrierId: number;
  carrierName: string;
  carrierSlug: string;
  autoApprovalEnabled: boolean;
  autoApprovalEnabledData: string;
  extracted_data: Record<string, Record<string, boolean>>;
}

export interface AutoApprovalRate {
  carrierId: number;
  carrierName: string;
  approvedSubmissionCount: string;
  percentTotalApproved: string;
  percentAutoApproved: string;
}

export interface PagedOwlResponseMeta {
  current_page: number;
  from: number;
  last_page: number;
  links: {
    url?: string;
    label: string;
    active: boolean;
  }[];
  path: string;
  per_page: number;
  to: number;
  total: number;
}

export interface PagedOwlResponse<T, U = any> extends OwlResponse<T> {
  links: {
    first: string;
    last: string;
    prev?: string;
    next?: string;
  };
  meta: PagedOwlResponseMeta & U;
}

export enum PublicSetting {
  ShowDriversForm = 'show_driver_information_form',
  MonthlySubmissionGoal = 'monthly_submission_goal',
  CommercialLines = 'commercial_lines',
  PersonalLines = 'personal_lines',
  ClientSubmissionNotifications = 'client_submission_notifications',
  ShowAcceleratedUnderwriting = 'show_accelerated_underwriting_questions',
  PolicyCancellationRequest = 'policy_cancellation_request',
  ShowBeagleOffer = 'show_beagle_offer',
  ShowIntakeForm = 'show_intake_form',
  PolicyResyncNotifications = 'policy_resync_notifications',
}

export type PolicyResyncNotificationEvent =
  | 'cancelled'
  | 'non_renewed'
  | 'renewed'
  | 'material'
  | 'sync_paused';

export type PolicyResyncRecipientsMode = 'client_agent' | 'admins' | 'selected';

export interface PolicyResyncNotificationsValue {
  events: Record<PolicyResyncNotificationEvent, boolean>;
  recipients: { mode: PolicyResyncRecipientsMode; agentIds: number[] };
}

export enum FreeTrialType {
  Usage = 'usage',
  Time = 'time',
}

export interface ClientAddress extends BasicAddressDetails {
  uuid: string;
}

export interface CancellationPolicy {
  policyId: string;
  policyType: { id: number; name: string };
  carrier: Carrier;
  cancellationDate: Date;
}

export interface PolicyCancellationUpdate {
  clientAddressUuid: string;
  priorAgentFirstName: string;
  priorAgentLastName: string;
  priorAgentAgencyName: string;
  priorAgentEmail: string;
}

export interface PolicyCancellationRequest {
  uuid: string;
  client: Client;
  address: ClientAddress | null;
  priorAgentFirstName: string | null;
  priorAgentLastName: string | null;
  priorAgentAgencyName: string | null;
  priorAgentEmail: string | null;
  cancellationRequestFilePath: string | null;
  status: string;
  createdAt: Date;
  policies: CancellationPolicy[];
}

export interface PolicyCancellationPolicyPayload {
  uuid: string;
  cancellationDate: Date;
}

export interface PolicyCancellationRequestPayload {
  policies: PolicyCancellationPolicyPayload[];
  clientSubmissionUuid: string;
}

export interface Agency {
  name: string;
  websiteUrl?: string;
  slugPrefix: string;
  logoUrl?: string;
  colorCode?: string;
  id: number;
  isActive?: boolean;
  /** Set by owl. Absent on older payloads — treat absent as allowed. */
  hasActiveSubscription?: boolean;
  freeTrial: {
    enabled: boolean;
    terms?: {
      type: FreeTrialType;
      limit: number;
    };
  };
}

export interface AgentProfile {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string | null;
  profilePictureUrl?: string;
}

export interface AgentPublicSettings {
  key: PublicSetting;
  value: any;
}

export type SubmissionSource = 'email';

export interface AgentInfo extends AgentProfile {
  id: number;
  agencyId?: number;
  externalIdentifier: string;
  /** INS-1563: `<inbound_email_alias>@upload.insurgrid.com`; null when Owl can't offer one. */
  inboundEmailAddress?: string | null;
  slug: string;
  slugSuffix?: string;
  isAdmin: boolean;
  isOwner?: boolean;
  isActive: boolean;
  onboardingComplete: boolean;
  publicSettings: AgentPublicSettings[];
  intercomUserHash: string;
  onboardingCompletedAt: string; // 2022-04-29T15:04:38Z
  completedPersonaSurvey: boolean;
}

export interface ConstituentInfo {
  id: number;
  firstName: string;
  lastName: string;
  isActive: boolean;
}

export type ModalAgentInfo = Omit<
  AgentInfo,
  | 'onboardingComplete'
  | 'publicSettings'
  | 'intercomUserHash'
  | 'onboardingCompletedAt'
  | 'completedPersonaSurvey'
>;

/**
 * How a driver relates to the named insured, as ACORD's personal-auto
 * applications enumerate it (INS-1418).
 *
 * The list constrains the pickers only — the field is a free-text string
 * everywhere it is stored, because phoenix's dec-page parser fills the same
 * concept (`relationship_to_policyholder`) from an LLM reading a carrier PDF
 * and cannot be held to this list. Treat a value outside it as valid data from
 * that source, never as corruption.
 *
 * Order is deliberate: the most common answers come first, since the client
 * picking from this dropdown is usually the policyholder adding their own
 * household.
 */
export const DRIVER_RELATIONSHIP_OPTIONS = [
  'Insured',
  'Spouse',
  'Domestic partner',
  'Child',
  'Parent',
  'Sibling',
  'Other relative',
  'Employee',
  'Other',
] as const;

export type DriverRelationship = (typeof DRIVER_RELATIONSHIP_OPTIONS)[number];

export interface Driver {
  uuid: string;
  source: DataSource;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  driverLicenseCode: string;
  usStateCode: string;
  /**
   * Optional: every driver recorded before INS-1418 has none, and automation
   * can create one without ever asking. Widened to `string` rather than
   * `DriverRelationship` because owl also serves values extracted from dec
   * pages, which are not drawn from the list above.
   */
  relationshipToPolicyholder?: string | null;
}

export interface PolicyClaim {
  uuid: string;
  claimId: string | null; // carrier-provided claim number
  date: string | null; // 'YYYY-MM-DD'
  status: string | null; // carrier-provided free-text status
  amount: string | null; // raw numeric string
  metadata?: Record<string, unknown> | null;
  createdAt: string; // ISO timestamp
}

export interface SubmissionDocument {
  uuid: string;
  documentType: string;
  documentLabel: string;
  publicUrl: string;
  downloadUrl?: string;
  documentDate?: string;
}

export interface Policy {
  cancellationDate: string;
  cancellationReason: string | null;
  createdAt: Date;
  effectiveDate: string;
  expirationDate: string;
  isThirdPartyCarrier: 0 | 1;
  mailingAddress: BasicAddressDetails;
  policyId: string;
  premium: string;
  policyType: {
    id: number;
    name: OwlPolicyType;
  };
  thirdPartyCarrierName: string | null;
  uuid: string;
  policyHolder: PolicyHolder;
  drivers: Driver[];
  dwellings: DwellingInformation[];
  vehicles: PolicyVehicle[];
  coverages: PolicyCoverage[];
  claims?: PolicyClaim[];
  documents?: SubmissionDocument[];
  lastSyncedAt?: string | null;
  lastSyncChanged?: boolean;
}

export type PolicyChangeEvent =
  | 'cancelled'
  | 'reinstated'
  | 'renewed'
  | 'non_renewed'
  | 'material';

export type PolicyChangeTrigger = 'scheduled' | 'manual' | 'restore';

export interface PolicyFieldChange {
  path: string;
  label: string;
  old: string | null;
  new: string | null;
  summary?: string;
}

export interface PolicyChangeVersion {
  version: number;
  syncedAt: string;
  trigger: PolicyChangeTrigger;
  events: PolicyChangeEvent[];
  changes: PolicyFieldChange[];
}

export type ResyncSuspendedReason =
  | 'mfa_required'
  | 'invalid_credentials'
  | 'repeated_failures'
  | 'account_locked'
  | 'restored';

export interface Submission {
  uuid: string;
  carrierSlug: string;
  status: OWL.SubmissionStatus;
  credentialsStatus?: string;
  declarationPages: DeclarationPage[];
  documents?: SubmissionDocument[];
  policies: Policy[];
  carrier: {
    name: string;
    slug: string;
    websiteUrl: string;
    loginUrl: string;
    logo: string;
  };
  submittedAt: string;
  submittedAtHuman: string;
  isManualUpload: boolean;
  /** INS-1563: `email` when the documents arrived at the agent's inbound address. */
  source?: SubmissionSource | null;
  approvedAt?: string;
  approvedBy?: string;
  messageForAgent?: string;
  /** Origin session traceId, copied from the parent Client by Owl. */
  traceId?: string;
  resyncSuspendedReason?: ResyncSuspendedReason | null;
}

export interface LoginData {
  email: string;
  password: string;
}

export interface ValidateTokenData {
  token: string;
  signature: string;
}

export interface TokenData {
  accessToken: string;
  tokenType: string;
}

export interface PolicyComparisonApiKey {
  id: number;
  name: string;
  lastUsedAt: string | null;
  createdAt: string;
}

export interface CreatedPolicyComparisonApiKey extends PolicyComparisonApiKey {
  key: string;
}

export interface SetPasswordDetails {
  token: string;
  email: string;
  password: string;
  password_confirmation: string;
}

export interface User {
  firstName: string;
  lastName: string;
  email: string;
  emailVerifiedAt?: string;
  createdAt: string;
  lastLoginAt?: string;
  isInsurGridAdmin: boolean;
  agent?: AgentInfo & { agency: Agency };
}

export interface ImpersonationUser
  extends Pick<User, 'firstName' | 'lastName' | 'email'> {
  id: number;
}

export interface DeclarationPage {
  id: number;
  uuid: string;
  publicUrl: string;
  downloadUrl: string;
  policyType?: {
    id: number;
    name: string;
  };
  policyStartDate?: string;
  policyEndDate?: string;
  automationData?: {
    premium?: number;
    policyId?: string;
    policyType?: string;
    policyEndDate?: string;
    policyStartDate?: string;
  };
  policyId: string;
  policyTypeId?: string;
  premium?: number;
  isManualUpload: boolean;
  uploadedAt: string;
  uploadedBy?: string;
}

export interface DocumentUpload {
  uuid: string;
  carrierSlug: string;
  status: string;
  carrier: {
    name: string;
    slug: string;
  };
  declarationPages: DeclarationPage[];
  submittedAt: string;
}

export interface PersonalInfo {
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  /** Passed to us by 3rd party, so they can track users in our system */
  externalId?: string;
  gender?: string;
  maritalStatus?: string;
  occupation?: string;
  industry?: string;
  educationLevel?: string;
  notes?: string;
  isTestClient?: boolean;
}

export interface AuthUpdateClientPayload {
  notes?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
}

export interface EmailPreview {
  subject: string;
  body: string;
}

export interface SMSPreview {
  body: string;
}

export interface SendSMS {
  clientId: number;
  clientSubmissionId: null | string;
  medium: ContactMedium;
  type: string;
  purpose: string;
  viewed: null | boolean;
  clicked: false;
  createdAt: string;
}

export interface ClientNotification {
  clicked: boolean;
  clientId: number;
  clientSubmissionId: string | null;
  createdAt: string;
  purpose: string;
  type: string;
  viewed: string;
  medium: ContactMedium;
}

export interface DriversData extends Driver {
  uuid: string;
  source: DataSource.ClientEntry | DataSource.Automation;
}

export enum DataEntryStatus {
  NotAsked = 'not_asked',
  Complete = 'complete',
  Prompted = 'prompted',
  Requested = 'requested',
}

export interface Client extends PersonalInfo {
  uuid: string;
  agent: AgentInfo;
  drivers: Driver[];
  archivedAt: string;
  updatedAt: string;
  createdAt: string;
  isArchived: boolean;
  clientStatedTheyHaveNoDrivers: boolean;
  clientStatedTheyHaveNoDwellings: boolean;
  manualDataEntryStatus: DataEntryStatus;
  clientNotifications: ClientNotification[];
  clientSubmissions: Submission[];
  status: 'agent_initiated' | 'ready' | 'prospect' | 'initialized';
  insuranceLine: LineType;
  salesFunnelStatus: keyof SalesFunnelStatus;
  actualPremium: number | null;
  testClient?: boolean;
  hasLeadFormResponses?: boolean;
  hasCompletedLeadFormResponses?: boolean;
  dwellings: DwellingInformation[];
  vehicles: PolicyVehicle[];
  attributionSource: string | null;
  customLink?: CustomLink;
  /** Origin session traceId, set at client.create from the Parrot funnel. */
  traceId?: string;
  additionalQuestionResponses?: AgentQuestions | null;
  agentInitiated?: AgentInitiated;
  policyCancellationRequests: PolicyCancellationRequest[];
}

export enum TrackingType {
  View = 'view',
  Click = 'click',
}

export interface AgentInitiated {
  createdAt: string;
  medium: ContactMedium;
  tracking: {
    action: TrackingType;
    createdAt: string;
  }[];
}

export type AgentQuestions = Record<string, string>;

export interface AgentQuestion {
  question: string;
  response?: string;
  uuid: string;
}

export interface SalesFunnelStatus {
  pending: 'Pending';
  quoted: 'Quoted';
  closed_won: 'Closed Won';
  closed_lost: 'Closed Lost';
}

export enum InvoiceStatus {
  Draft = 'draft',
  Open = 'open',
  Paid = 'paid',
  Void = 'void',
  Uncollectible = 'uncollectible',
}

export interface Invoice {
  id: string;
  description: string | null;
  amount: number;
  status: InvoiceStatus;
  pdf: string;
  date: string;
}

export enum StripeStatus {
  Unpaid = 'unpaid', // Subscription has not been paid within a certain period
  IncompleteExpired = 'incomplete_expired', // Invoice not paid within 23 hours
  Active = 'active', // Payment is good
  Canceled = 'canceled',
  PastDue = 'past_due', // Cannot be paid (failed payment or awaiting user action)
  Trialing = 'trialing',
  Incomplete = 'incomplete', // Initial payment attempt failed
}

export interface SubscriptionDetails {
  subscriptionName: string | null;
  churnkeyAuthHmac: string | null;
  subscriptionStatus: StripeStatus;
  stripeId: string;
  subscriptionId: string;
  currentPaymentMethod: {
    card: Card;
  };
}

export type Card = {
  last4: string;
  brand: string;
  expirationMonth: number;
  expirationYear: number;
};

export enum ContactMedium {
  Sms = 'sms',
  Email = 'email',
}

export type ClientEmailDetails = {
  email: string;
  carbonCopyAgent: boolean;
  customTemplateId?: number;
  medium: ContactMedium.Email;
  firstName: string;
  lastName: string;
  testClient?: boolean;
  templateKey: string;
  customLinkId?: number;
  customLinkUrl?: string;
  showAcceleratedUnderwritingQuestions?: boolean;
};

export type ClientSmsDetails = {
  mobilePhone: string;
  medium: ContactMedium.Sms;
  firstName: string;
  lastName: string;
  testClient?: boolean;
  templateKey: string;
  carbonCopyAgent: boolean;
  customTemplateId?: number;
  customLinkId?: number;
  customLinkUrl?: string;
  showAcceleratedUnderwritingQuestions?: boolean;
};

export interface EmailTemplateOption {
  key: string;
  label: string;
  customTemplateId?: number;
}

export interface EmailTemplateDetails {
  templateKey: string;
  templateLabel: string;
  subject: string;
  body: string;
}

export interface VariableParameter {
  variable: string;
  parameter: string;
  value: string;
}

export interface CustomTemplate {
  insuranceLine?: LineType;
  name: string;
  subject: string;
  body: string;
  variableParameters: VariableParameter[];
  medium: ContactMedium;
}

export interface CustomTemplatePreviewDetails {
  firstName: string;
  lastName: string;

  templateKey: 'custom' | string;
  insuranceLine: LineType;
  customTemplateId?: number;
  subject: string;
  body: string;
  variableParameters: VariableParameter[];
}

export interface CustomEmailTemplatePreviewDetails
  extends CustomTemplatePreviewDetails {
  email: string;
  medium: ContactMedium.Email;
}

export interface CustomSmsTemplatePreviewDetails
  extends CustomTemplatePreviewDetails {
  mobilePhone: string;
  medium: ContactMedium.Sms;
}

export interface CustomTemplateDetails extends CustomTemplate {
  id: number;
}

export interface AgentIncentiveInfo {
  progress: number;
  goal: number;
  goalAchieved: boolean;
  daysLeft: number;
  firstMonthIncentive: boolean;
  message: string;
  startDate: string;
  endDate: string;
}

export enum RenewalFrequency {
  Monthly = 'monthly',
  Annually = 'annual',
}

export interface PricingOption {
  term: RenewalFrequency;
  label: 'Monthly' | 'Annually';
  amountPerMonth: '99' | '79.20';
  description: 'Billed monthly.' | 'Billed annually. (20% Saving!)';
}

export enum OwlPolicyType {
  Auto = 'Auto',
  Condo = 'Condo',
  Homeowners = 'Homeowners',
  Other = 'Other',
  Renters = 'Renters',
  Umbrella = 'Umbrella',
}

export type ClientListResponse = PagedOwlResponse<
  Client[],
  {
    counts: {
      filters: {
        complete: number;
        incomplete: number;
        requires_attention: number;
      };
    };
    totals: {
      decPages: number;
      premium: number;
      hoursSaved: number;
      policyCountsByType: {
        policyCount: number;
        policyType: OwlPolicyType;
      }[];
    };
  }
>;

// query params to use in parrot
export enum AcceleratedUnderwritingQuery {
  Drivers = 'askDrivers',
  Property = 'askProperty',
}

export interface CustomLink {
  uuid: string; // '3c13547d-261f-464e-96c7-5c1c578f651e';
  label: string; // 'Yvette McGlynn';
  linkName: string; // 'Yvette McGlynn';
  linkDescription: string | null; // null;
  askClientDriver: boolean | null; // null;
  askClientProperty: boolean | null; // null;
  referralPartnerName: string | null; // null;
  referralPartnerCompany: string | null; // null;
  referralPartnerLogoUrl: string | null; // null;
  referralPartnerHeadshotUrl: string | null; // null;
  askHowDidYouHear: string | null; // null;
  slug: string; // 'dr-lula-donnelly';
  customLinkUrl: string; // 'https://link-staging.insurgrid.com/free-trial-test?customLink=dr-lula-donnelly';
  clientCount?: number; // 0;
  createdAt: string; // '2022-08-17T20:30:49Z';
  archivedAt: string | null; // null;
  referralPartnerJobTitle: string | null;
  additionalQuestions?: Record<string, string> | null;
  customLinkId?: number;
  insuranceLine?: LineType;
}

export interface CustomLinkStats {
  email: {
    approved_clients: number;
    total_clicks: number;
    total_clients: number;
    total_views: number;
  };
  sms: {
    approved_clients: number;
    total_clicks: number;
    total_clients: number;
  };
  other: {
    approved_clients: number;
    total_visits: number;
  };
}

export interface AttributionSource {
  attributionSource: string;
  customLinkUuid: string;
}

export type CreateClientRequest = Partial<PersonalInfo> & {
  agentSlug: string;
  insuranceLine: LineType;
  /** Origin session traceId; Owl copies it onto the client. */
  traceId?: string;
};

export type AgentWithAgency = AgentInfo & { agency: Agency };

export type DocumentUploadResponse = OwlResponse<DocumentUpload>;
export type LoginResponse = OwlResponse<TokenData>;
export type UserResponse = OwlResponse<User>;
export type SubmissionResponse = OwlResponse<
  Omit<Submission, 'declarationPages'>
>;
export type DriversResponse = OwlResponse<DriversData[]>;
export type ClientResponse = OwlResponse<Client>;
export type AgentInfoWithAgencyResponse = OwlResponse<AgentWithAgency>;
export type AgentCustomLinkDetailsResponse = OwlResponse<CustomLink>;
export type AgentInfoResponse = OwlResponse<AgentInfo>;
export type AgencyUpdateResponse = OwlResponse<Agency>;
export type SubscriptionDetailsResponse = OwlResponse<SubscriptionDetails>;

export interface SubscriptionStatusInfo {
  subscriptionStatus: StripeStatus | null;
  isPaused: boolean;
}
export type SubscriptionStatusResponse = OwlResponse<SubscriptionStatusInfo>;
export type AgencyTeamInfoResponse = PagedOwlResponse<AgentInfo[]>;
export type ConstituentListResponse = PagedOwlResponse<ConstituentInfo[]>;
export type AgentEmailSentResponse = OwlResponse<ClientEmailDetails>;
export type AgentEmailTemplateResponse = OwlResponse<EmailTemplateDetails>;
export type AgentCustomTemplateResponse =
  OwlResponse<CustomTemplatePreviewDetails>;
export type AgentCustomTemplateDetailsResponse =
  OwlResponse<CustomTemplateDetails>;
export type AgentCreateCustomTemplateResponse =
  OwlResponse<CustomTemplateDetails>;
export type AgentUpdateCustomTemplateResponse =
  OwlResponse<CustomTemplateDetails>;
export type AgentDeleteCustomTemplateResponse =
  OwlResponse<CustomTemplateDetails>;
export type ClientNotificationResponse = OwlResponse<ClientNotification>;
export type SendSMSResponse = OwlResponse<SendSMS>;
export type EmailPreviewResponse = OwlResponse<EmailPreview>;
export type SMSPreviewResponse = OwlResponse<SMSPreview>;
export type AgencySubscriptionPricingOptionsResponse = OwlResponse<
  PricingOption[]
>;
export type AgentEmailTemplateOptionsResponse = OwlResponse<
  EmailTemplateOption[]
>;
export type AgentCompletedTasksResponse = OwlResponse<string[]>;
export type AgentUpdateLinkSettingsResponse = OwlResponse<
  Record<PublicSetting, any>[]
>;
export type AgentIncentiveInfoResponse = OwlResponse<AgentIncentiveInfo>;
export type CarrierListResponse = OwlResponse<OWL.Carrier[]>;
export type SalesFunnelStatusUpdateResponse = OwlResponse<
  Omit<Client, 'clientSubmissions' | 'drivers' | 'agent'> & {
    actualPremium: number | null;
    createdAt: string;
  }
>;
export type StripePaymentIntentResponse = OwlResponse<{
  paymentIntentSecret: string;
}>;

export interface SeatsInfo {
  includedSeats: number;
  purchasedSeats: number;
  totalSeats: number;
  usedSeats: number;
  availableSeats: number;
  maxTotalSeats: number;
  pricePerSeat: string;
  billingTerm: string;
  defaultPaymentMethod: { brand: string; last4: string } | null;
  basePlanPrice: string | null;
  totalMonthly: string | null;
}

export interface SeatsPricingInfo {
  monthlyPrice: string;
  annualPrice: string;
  proratedChargeToday: string;
  billingCycleEnd: string;
}

export type SeatsInfoResponse = OwlResponse<SeatsInfo>;
export type SeatsPricingInfoResponse = OwlResponse<SeatsPricingInfo>;

export interface CancelProfitwellResponse {
  status: 'error' | 'aborted' | 'chose_to_cancel' | 'retained';
  salvageAttemptResult: {
    decision: 'accepted' | 'rejected';
    resolution: 'accepted' | 'rejected';
    hasErrors: boolean;
  } | null;
  salvageOfferResult: {
    decision: 'accepted' | 'rejected';
    hasErrors: boolean;
  } | null;
  additionalFeedback: string | null;
  cancelReason: string | null;
  satisfactionInsight: string | null;
  salvageAttemptIntended:
    | 'pause_subscription'
    | 'plan_switch'
    | 'contact_support_email_notification'
    | 'contact_support_meeting_scheduler'
    | null;
  salvageAttemptUsed:
    | 'pause_subscription'
    | 'plan_switch'
    | 'contact_support_email_notification'
    | 'contact_support_meeting_scheduler'
    | null;
}

export interface AppWindow extends Window {
  Intercom?: (
    action: 'boot' | 'update' | 'showArticle' | 'showNewMessage' | 'trackEvent',
    options?: Record<string, any> | string,
    metadata?: Record<string, any>,
  ) => void;
  intercomSettings?: {
    app_id: string;
    custom_launcher_selector: '#custom_intercom_button';
  };
  analytics?: SegmentAnalytics.AnalyticsJS;
  hj?: (
    action: 'identify',
    userId: string,
    options?: {
      clientId?: string;
      clientSubmissionId?: string;
      traceId?: string;
    },
  ) => void;
}

export enum ProgressStatus {
  Upcoming = 'upcoming',
  Current = 'current',
  Complete = 'complete',
  Skipped = 'skipped',
  Incomplete = 'incomplete',
}

export enum STEP {
  // personal = 'personal',
  submission = 'submission',
  driver = 'driver',
  property = 'property',
  summary = 'summary',
  finished = 'success',
  agentQuestions = 'agentQuestions',
}

export interface TrainingData {
  input: { v1: string; v2: string };
  output: {
    carrierName: string;
    address?: PrimaryAddress;
    startDate?: string;
    endDate?: string;
    premium?: number;
    discounts?: Discount[];
    coverages?: Coverage[];
    drivers: Omit<DriverResource, 'resourceType' | 'resourceId'>[];
    vehicles: {
      name: string;
      make?: string;
      model?: string;
      year?: string;
      vin?: string;
      zipCode?: string;
      primaryUse?: string;
      coverages?: Coverage[];
      discounts?: Discount[];
    }[];
    policyId: string;
  };
}
